/**
 * Copyright 2016 xdot GmbH
 * Author: Marius Runde
 * Date: 29.04.2016
 *
 */
'use strict';

import React from 'external/react';
import PropTypes from 'external/prop-types';
import { Col, Container, Row } from 'external/react-bootstrap';
import {
    AmdRequireComponent,
    ConfirmFormContainer,
    FormActions,
    ValidationMessage,
    wrapWithCommonFormContainer
} from 'plugin/liferay-widgets-react-web';
import Icon from './Icon';
import MapConfigEditLayerForm from './MapConfigEditLayerForm';
import MapConfigLogicalLayerChooser from './MapConfigLogicalLayerChooser';
import { isLayerSet, isLogicalLayer, loopLogicalLayerIds } from '../utils/map-config-util';

const loop = (data, parent, position, callback) => {
    data.forEach((item, index) => {
        if (item.position === position) {
            return callback(item, index, parent);
        } else if (item.children) {
            return loop(item.children, item, position, callback);
        }
    });
};

let MapConfigEditLayerFormHoc = null;

class MapConfigLayerTree extends React.Component {

    constructor(props){
        super(props);

        let layers = [
            {
                title: 'Layer Sets',
                type: 'LayerSet',
                root: true
            }
        ];
        const { layerSets = [] } = this.props;
        layers[0].children = layerSets;

        MapConfigEditLayerFormHoc = wrapWithCommonFormContainer(MapConfigEditLayerForm);

        this.DATA = {
            FORM_MAP_CONFIG_EDIT_LAYER_FORM: 'MapConfigEditLayerForm',
            FORM_MAP_CONFIG_DELETE_LAYER_FORM: 'MapConfigDeleteLayerForm'
        };

        const selectedLogicalLayers = loopLogicalLayerIds(layers[0], []);

        this.state = {
            layers,
            selectedLogicalLayers,
            selectedKeys: []
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps){
        const { layerSets = [] } = nextProps;
        const nextLayers = this.state.layers;
        nextLayers[0].children = layerSets;

        const selectedLogicalLayers = loopLogicalLayerIds(nextLayers[0], []);

        this.setState({
            layers: nextLayers,
            selectedLogicalLayers,
            selectedKeys: []
        });
    }

    onDragEnter(info){
        this.setState({
            expandedKeys: info.expandedKeys
        });
    }

    onDrop(info){
        const dropPos = parseInt(info.node.position);
        const dragPos = parseInt(info.dragNode.position);

        let { layers } = this.state;

        let dragObj, prevIndex, prevParent, dropObj, dropToGap;

        loop(layers, null, dragPos, (item, index, parent) => {
            dragObj = item;
            prevIndex = index;
            prevParent = parent;
        });

        loop(layers, null, dropPos, (item, index, parent) => {
            // Only layer sets allowed on root level
            if ((item.root && isLayerSet(dragObj)) || (!item.root)) {
                if (info.dropToGap) {
                    dropObj = parent;
                    dropToGap = true;
                } else if (isLayerSet(item)) {
                    dropObj = item;
                    dropToGap = false;
                }
            }
        });

        if (dropObj) {
            prevParent.children.splice(prevIndex, 1);

            if (dropToGap) {
                dropObj.children.splice(info.dropPosition + 1, 0, dragObj);
            } else {
                dropObj.children.push(dragObj);
            }
        }

        this.setState({
            layers,
            selectedKeys: [],
            selectedLayerSet: null
        });
    }

    onExpand(expandedKeys){
        this.setState({
            expandedKeys
        });
    }

    onSelect(selectedKeys, e){
        let { layers, selectedLayerSet } = this.state;

        let layerPosition = parseInt(e.node.position);

        loop(layers, null, layerPosition, (item) => {
            if (!item.root && isLayerSet(item) && (!selectedLayerSet || layerPosition !== selectedLayerSet.position)) {
                this.setState({
                    selectedKeys,
                    selectedLayerSet: item
                });
            } else {
                this.setState({
                    selectedKeys,
                    selectedLayerSet: null
                });
            }
        });
    }

    onAddLayerSet(parentLayerSet, e){
        e.stopPropagation();

        FormActions.startEditFormItem(this.DATA.FORM_MAP_CONFIG_EDIT_LAYER_FORM, { isNew: true });
        this.setState({
            selectedLayerSet: parentLayerSet
        });
    }

    onEditLayerSet(layerSet, e){
        e.stopPropagation();

        FormActions.startEditFormItem(this.DATA.FORM_MAP_CONFIG_EDIT_LAYER_FORM, layerSet);
        this.setState({
            selectedLayerSet: layerSet
        });
    }

    handleSubmitEditLayerSetModal(formData){
        const { title, description, isNew, config } = formData;

        let { layers, selectedLayerSet } = this.state;

        if (isNew) {
            let newLayerSet = {
                title,
                type: 'LayerSet',
                description,
                children: [],
                config
            };

            loop(layers, null, selectedLayerSet.position, function (layerSet){
                layerSet.children.push(newLayerSet);
            });
        } else {
            loop(layers, null, selectedLayerSet.position, function (layerSet){
                layerSet.title = title;
                layerSet.description = description;
                layerSet.config = config;
            });
        }

        this.props.onChange(layers[0].children);
        FormActions.stopEditFormItem(this.DATA.FORM_MAP_CONFIG_EDIT_LAYER_FORM);

        this.setState({
            layers,
            selectedLayerSet: (isNew ? null : selectedLayerSet)
        });
    }

    onAddLogicalLayer(logicalLayer){
        let { layers, selectedLogicalLayers, selectedLayerSet } = this.state;

        if (selectedLogicalLayers.indexOf(logicalLayer.uuid) < 0) {
            let copyOfLogicalLayer = { ...logicalLayer };
            delete copyOfLogicalLayer.children;

            if (selectedLayerSet) {
                loop(layers, null, selectedLayerSet.position, (layerSet) => {
                    layerSet.children.push(copyOfLogicalLayer);
                    selectedLogicalLayers.push(copyOfLogicalLayer.uuid);
                });

                this.props.onChange(layers[0].children);

                this.setState({
                    layers,
                    selectedLogicalLayers
                });
            }
        }
    }

    onDelete(layer, e){
        if (e) {
            e.stopPropagation();
        }

        FormActions.startEditFormItem(this.DATA.FORM_MAP_CONFIG_DELETE_LAYER_FORM, layer);

        this.setState({
            selectedLayer: layer,
            showDeleteLayerModal: true
        });
    }

    handleSubmitDeleteLayerModal(){
        let { layers, selectedLogicalLayers, selectedLayer } = this.state;

        loop(layers, null, selectedLayer.position, function (item, index, parent){
            parent.children.splice(index, 1);
        });

        if (isLogicalLayer(selectedLayer)) {
            selectedLogicalLayers.splice(selectedLogicalLayers.indexOf(selectedLayer.uuid), 1);
        }

        this.props.onChange(layers[0].children);
        FormActions.stopEditFormItem(this.DATA.FORM_MAP_CONFIG_DELETE_LAYER_FORM);

        this.setState({
            layers,
            selectedLogicalLayers,
            selectedLayer: null,
            showDeleteLayerModal: false
        });
    }

    renderLayerSet(item){
        return (
            <span>

                <span>{item.root ? 'Layer Sets' : item.title}</span>
                {' '}
                <Icon name="plus" onClick={this.onAddLayerSet.bind(this, item)}/>
                {' '}
                {!item.root && <Icon name="pencil" onClick={this.onEditLayerSet.bind(this, item)}/>}
                {' '}
                {!item.root && <Icon name="x" onClick={this.onDelete.bind(this, item)}/>}

            </span>
        );
    }

    renderLogicalLayer(item){
        return (
            <span>

                <span>{item.title}</span>
                {' '}
                <Icon name="x" onClick={this.onDelete.bind(this, item)}/>

            </span>
        );
    }

    getTreeData(layers, level = 0) {
        return layers.map((item, i) => {
            const key = `${level}-${i}`;
            if (isLayerSet(item)) {
                return {
                    key,
                    title: () => this.renderLayerSet(item),
                    isLeaf: false,
                    children: this.getTreeData(item.children, key),
                    position: item.position
                };
            } else {
                return ({
                    key,
                    title: () => this.renderLogicalLayer(item),
                    isLeaf: true,
                    position: item.position
                });
            }
        });
    }

    render(){
        const {
            layers,
            selectedLogicalLayers,
            selectedKeys,
            selectedLayerSet,
        } = this.state;

        const { formValidation } = this.props;

        let counter = 0;

        const appendPos = (arr) => {
            arr.forEach((i) => {
                let item = i.item || i;
                if (isLayerSet(item) || isLogicalLayer(item)) {
                    item.position = counter;
                    counter++;
                }

                if (item.children) {
                    appendPos(item.children);
                }
            });
        };

        appendPos(layers);

        return (
            <Container>
                <Row>
                    <Col lg={6} md={6} sm={12} xs={12}>
                        <h3>Layer-Gruppierung</h3>

                        <div style={{ height: 500, overflow: 'auto' }}>
                            <AmdRequireComponent dependencies={['rc-tree', 'rcTreeCss']}>
                                {
                                    (rcTree) => {
                                        const { 'default': Tree } = rcTree;
                                        const treeData = this.getTreeData(layers);
                                        return (
                                            <Tree
                                                selectedKeys={selectedKeys}
                                                onExpand={this.onExpand.bind(this)}
                                                autoExpandParent={true}
                                                defaultExpandAll
                                                showLine
                                                draggable
                                                onDragEnter={this.onDragEnter.bind(this)}
                                                onDrop={this.onDrop.bind(this)}
                                                onSelect={this.onSelect.bind(this)}
                                                treeData={treeData}
                                            />
                                        );
                                    }
                                }
                            </AmdRequireComponent>
                        </div>
                        <ValidationMessage formValidation={formValidation} propName="children"/>


                        <MapConfigEditLayerFormHoc
                            formId={this.DATA.FORM_MAP_CONFIG_EDIT_LAYER_FORM}
                            onFormSubmit={this.handleSubmitEditLayerSetModal.bind(this)}
                        />

                        <ConfirmFormContainer
                            formId={this.DATA.FORM_MAP_CONFIG_DELETE_LAYER_FORM}
                            editRequired={false}
                            onFormSubmit={this.handleSubmitDeleteLayerModal.bind(this)}
                            Content={({ formData }) => (
                                ((formData.item || formData).type === 'LayerSet') ?
                                    'Wollen Sie die Gruppierung ' + (formData.item || formData).title + ' wirklich löschen?' :
                                    'Wollen Sie den Layer ' + (formData.item || formData).title + ' wirklich löschen?'
                            )}
                        />

                    </Col>

                    <Col lg={6} md={6} sm={12} xs={12}>
                        <MapConfigLogicalLayerChooser
                            selectedLogicalLayers={selectedLogicalLayers}
                            selectedLayerSet={selectedLayerSet}
                            logicalLayers={this.props.logicalLayers}
                            onSelect={this.onAddLogicalLayer.bind(this)}
                        />
                    </Col>
                </Row>
            </Container>
        );
    }
}

MapConfigLayerTree.propTypes = {
    layerSets: PropTypes.array,
    logicalLayers: PropTypes.object,
    formValidation: PropTypes.object,
    onChange: PropTypes.func
};

export default MapConfigLayerTree;



// WEBPACK FOOTER //
// ./js/components/MapConfigLayerTree.jsx